import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import {
  translate,
  TranslocoModule,
  TranslocoService,
} from '@jsverse/transloco';
import { dispatch } from '@ngneat/effects';
import {
  CONTENT_TOOLS,
  MAP_EXACT,
  MAP_SURE,
  PCM_WEB,
  ROUTE_REPORTER,
} from '@shared/core-utils';
import { environmentProvider } from '@shared/environment-provider';
import { getCurrentAuthenticatedUser, logout } from '@tmx/auth';
import {
  APP_VERSION_TOKEN,
  InitializationState,
  SubscriptionManager,
} from '@tmx/core';
import {
  AlertComponent,
  AppShellComponent,
  AppShellService,
  ButtonComponent,
  FooterComponent,
  SideNavComponent as MopSideNavComponent,
  NavbarComponent,
  SpinnerComponent,
  Theme,
  ThemeToggleComponent,
  ToastOutletComponent,
  ToastService,
  TourProgressComponent,
  TourService,
} from '@tmx/modus-operandi';
import {
  ProfileDropdownComponent,
  WebToolItem,
  WebToolsDropdownComponent,
} from '@tmx/operandi';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { combineLatest, map } from 'rxjs';

import { BrazeCardViewerComponent } from './components/braze-card-viewer/braze-card-viewer.component';
import { OrderTrackingComponent } from './components/order-tracking/order-tracking.component';
import {
  deregisterSecondaryNavEffects,
  registerSecondaryNavEffects,
} from './components/secondary-nav/repositories/secondary-nav.effects';
import { resetSecondaryNavStore } from './components/secondary-nav/repositories/secondary-nav.repository';
import { SideNavComponent } from './components/side-nav/side-nav.component';
import { SupportChatComponent } from './components/support-chat/support-chat.component';
import { SwitchAccountsComponent } from './components/switch-accounts/switch-accounts.component';
import { hasPartnerRole } from './features/fleet-accounts/utils/has-partner-role';
import { READ_ONLY } from './models/ams-roles';
import { CompanyType } from './models/company-type';
import {
  ALK_PARTNER_ID,
  DEFAULT_PROFILE_SOURCE,
  DEFAULT_STAGING_PROFILE_SOURCE,
  TRIMBLE_BUCKET_PARTNER_REGEX,
} from './models/constants';
import { PrimaryNavKey } from './models/primary-nav-key';
import {
  appStoreSnapshot,
  isViewingChildAccount$,
  userAuthenticated$,
  userRoles$,
  userSwitchingAccounts$,
} from './repositories/account-manager/account-manager.repository';
import { registerAmsFeaturesEffects } from './repositories/ams-features/ams-features.effects';
import { amsFeaturesState$ } from './repositories/ams-features/ams-features.repository';
import {
  initBrazeInAppMessages,
  initializeAms,
  initPrimaryNavOptions,
  openBrazeSession,
  setTheme,
  switchAccountResetAms,
} from './repositories/ams/ams.actions';
import {
  deregisterAmsEffects,
  registerAmsEffects,
} from './repositories/ams/ams.effects';
import {
  accountSummary$,
  amsState$,
  onboardingShifted$,
  primaryNavOptions$,
  resetAmsStore,
  theme$,
  userInfo$,
  webToolItems$,
} from './repositories/ams/ams.repository';
import {
  initializeAppSettings,
  switchAccountResetAppSettings,
} from './repositories/app-settings/app-settings.actions';
import {
  deregisterAppSettingsEffects,
  registerAppSettingsEffects,
} from './repositories/app-settings/app-settings.effects';
import { resetAppSettingsStore } from './repositories/app-settings/app-settings.repository';
import { reloadAssets } from './repositories/assets/assets.actions';
import {
  deregisterAssetsEffects,
  registerAssetsEffects,
} from './repositories/assets/assets.effects';
import { subscribeToContentCards } from './repositories/content-cards/content-cards.actions';
import {
  deregisterContentCardsEffects,
  registerContentCardsEffects,
} from './repositories/content-cards/content-cards.effects';
import { resetContentCardsStore } from './repositories/content-cards/content-cards.repository';
import {
  deregisterDriversEffects,
  registerDriversEffects,
} from './repositories/drivers/drivers.effects';
import { toast$ } from './repositories/toasts.repository';
import {
  deregisterUserPreferencesEffects,
  registerUserPreferencesEffects,
} from './repositories/user-preferences/user-preferences.effects';
import {
  deregisterVehiclesEffects,
  registerVehiclesEffects,
} from './repositories/vehicles/vehicles.effects';
import {
  deregisterVidyardEffects,
  registerVidyardEffects,
} from './repositories/vidyard/vidyard.effects';
import { BrazeEventsService } from './services/braze-events.service';
import { OnboardingTourService } from './services/onboarding-tour.service';
import { filterOutLoadingState } from './utils/filter-out-loading-state';

@Component({
  selector: 'lib-account-manager',
  imports: [
    TranslocoModule,
    SpinnerComponent,
    OrderTrackingComponent,
    SwitchAccountsComponent,
    SupportChatComponent,
    SideNavComponent,
    TooltipModule,
    WebToolsDropdownComponent,
    RouterLink,
    RouterOutlet,
    AsyncPipe,
    ProfileDropdownComponent,
    BrazeCardViewerComponent,
    ButtonComponent,
    AppShellComponent,
    NavbarComponent,
    MopSideNavComponent,
    FooterComponent,
    ThemeToggleComponent,
    ToastOutletComponent,
    ButtonComponent,
    AlertComponent,
    TourProgressComponent,
  ],
  templateUrl: './account-manager.component.html',
  styleUrl: './account-manager.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountManagerComponent implements OnDestroy, OnInit {
  private readonly router = inject(Router);
  private readonly brazeEventsService = inject(BrazeEventsService);
  private readonly translocoService = inject(TranslocoService);
  private readonly toastService = inject(ToastService);
  private readonly appVersion = inject(APP_VERSION_TOKEN);
  private readonly tourService = inject(TourService);
  private readonly appShellService = inject(AppShellService);
  private readonly sm = new SubscriptionManager();
  private readonly onboardingTourService = inject(OnboardingTourService);

  componentState = signal<InitializationState>('loading');
  appVersionText = signal(
    translate('accountManager.accountManager.appVersionText', {
      version: this.appVersion,
    })
  );

  showOrderTracking$ = primaryNavOptions$.pipe(
    map((options) => options.get(PrimaryNavKey.Accounts) !== null)
  );
  showSwitchAccounts$ = combineLatest([
    userRoles$,
    accountSummary$,
    isViewingChildAccount$,
  ]).pipe(
    map(([userRoles, accountSummary, isViewingChildAccount]) => {
      return (
        isViewingChildAccount ||
        ((hasPartnerRole(userRoles) ||
          userRoles.some((role) => role === 'Fleet Support')) &&
          (accountSummary.companyType === CompanyType.Partner ||
            accountSummary.partnerId === ALK_PARTNER_ID))
      );
    })
  );
  currentUser = signal(getCurrentAuthenticatedUser());

  userRoles = computed(() =>
    this.currentUser()
      .roles.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0))
      .join(', ')
  );
  profileImgSource = toSignal(
    userInfo$.pipe(
      map(({ picture }) => picture),
      map((source) => {
        // The default profile image does not look good in the UI so we set to an empty string to
        // use the fallback
        if (
          source === DEFAULT_STAGING_PROFILE_SOURCE ||
          source === DEFAULT_PROFILE_SOURCE
        ) {
          return '';
        }

        return source;
      })
    ),
    {
      initialValue: '',
    }
  );

  accountSummary = toSignal(accountSummary$);
  companyName = computed(() => this.accountSummary()?.companyName);
  companyId = computed(() => this.accountSummary()?.companyCode);
  partnerInfo = toSignal(
    accountSummary$.pipe(
      map(({ partnerName, partnerId }) => {
        if (partnerId === '' || partnerId === null) {
          return null;
        }

        if (TRIMBLE_BUCKET_PARTNER_REGEX.exec(partnerId)) {
          return null;
        }

        return partnerId === '' ? partnerName : `${partnerName} (${partnerId})`;
      })
    ),
    {
      initialValue: null,
    }
  );
  externalAccountId = computed(() => this.accountSummary()?.externalAccountId);

  webToolItems$ = webToolItems$.pipe(
    map((items) => items.map((item) => new WebToolItem(item)))
  );
  isViewingChildAccount$ = isViewingChildAccount$;
  onboardingShifted = toSignal(onboardingShifted$, {
    initialValue: false,
  });

  currentYear = new Date().getFullYear();
  appShell = viewChild(AppShellComponent);

  constructor() {
    effect(() => {
      if (this.componentState() === 'initialized') {
        this.appShellService.setSideNavOpenState(true);

        const isReadOnly =
          getCurrentAuthenticatedUser().roles.includes(READ_ONLY);
        if (isReadOnly) {
          return;
        }

        this.onboardingTourService.initialize();
      }
    });
  }

  @HostListener('window:keydown.F5', ['$event'])
  onF5(event: KeyboardEvent) {
    const isReadOnly = getCurrentAuthenticatedUser().roles.includes(READ_ONLY);

    if (
      environmentProvider.getEnvironment().name !== 'prod' &&
      appStoreSnapshot().isOriginalLogin &&
      !isReadOnly
    ) {
      event.preventDefault();
      this.tourService.startTour();
    }
  }

  ngOnInit() {
    registerAppSettingsEffects();
    registerAmsEffects();
    registerAmsFeaturesEffects();
    registerSecondaryNavEffects();
    registerAssetsEffects();
    registerVehiclesEffects();
    registerDriversEffects();
    registerContentCardsEffects();
    registerVidyardEffects();
    registerUserPreferencesEffects();

    dispatch(initBrazeInAppMessages());
    dispatch(subscribeToContentCards());
    dispatch(openBrazeSession());

    dispatch(initializeAms());

    this.sm.manage = combineLatest([
      amsState$.pipe(filterOutLoadingState()),
      amsFeaturesState$.pipe(filterOutLoadingState()),
    ])
      .pipe(map((states) => states.every((state) => state === 'initialized')))
      .subscribe({
        next: (initialized) => {
          if (initialized) {
            this.componentState.set('initialized');
            dispatch(initPrimaryNavOptions());
          } else {
            this.componentState.set('failed');
          }
        },
      });

    this.sm.manage = toast$.subscribe({
      next: ({ type, content }) => {
        this.toastService.add({
          type: type ?? 'primary',
          content:
            typeof content === 'string'
              ? this.translocoService.translate(content)
              : '',
        });
      },
    });

    this.sm.manage = userAuthenticated$.subscribe({
      next: () => {
        dispatch(initializeAms());
        dispatch(reloadAssets());

        this.currentUser.set(getCurrentAuthenticatedUser());
      },
    });

    this.sm.manage = userSwitchingAccounts$.subscribe({
      next: () => {
        dispatch(switchAccountResetAppSettings());
        dispatch(switchAccountResetAms());
        this.tourService.endTour();
        this.componentState.set('loading');
      },
    });

    this.sm.manage = theme$.subscribe({
      next: (theme) => {
        this.brazeEventsService.logBrazeCustomEvents(
          theme === 'dark'
            ? this.brazeEventsService.DARK_MODE_ACTIVATED
            : this.brazeEventsService.LIGHT_MODE_ACTIVATED
        );
      },
    });
  }

  reInitialize() {
    dispatch(switchAccountResetAppSettings());
    dispatch(switchAccountResetAms());
    this.tourService.endTour();
    this.componentState.set('loading');

    dispatch(initializeAppSettings());
  }

  handleLogoClick() {
    this.router.navigateByUrl('/ams/loading');
  }

  openHelpUrl() {
    window.open(
      'https://learn.transportation.trimble.com/discover/result/?sub=LC/mappingroutingnavigation/accountmanager/onlinehelp/&tl=Account-Manager-Table-of-Contents&q=210204'
    );
    this.brazeEventsService.logBrazeCustomEvents(
      this.brazeEventsService.CLICKED_HELP_ICON
    );
  }

  handleWebToolsMenuClick() {
    this.brazeEventsService.logBrazeCustomEvents(
      this.brazeEventsService.CLICKED_WEBTOOLS_MENU
    );
  }

  handleWebToolItemClick(item: WebToolItem) {
    if (item.expired) {
      switch (item.name) {
        case CONTENT_TOOLS:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_EXPIRED_APP_CONTENT_TOOLS
          );
          break;
        case MAP_EXACT:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_EXPIRED_APP_MAPEXACT
          );
          break;
        case MAP_SURE:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_EXPIRED_APP_MAPSURE
          );
          break;
        case PCM_WEB:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_EXPIRED_APP_PC_MILER_WEB
          );
          break;
        case ROUTE_REPORTER:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_EXPIRED_APP_ROUTEREPORTER
          );
          break;
        default:
          break;
      }
    } else if (item.licensed) {
      switch (item.name) {
        case CONTENT_TOOLS:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.WEBTOOLS_MENU_NAVIGATED_TO_CONTENT_TOOLS
          );
          break;
        case MAP_EXACT:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.WEBTOOLS_MENU_NAVIGATED_TO_MAPEXACT
          );
          break;
        case MAP_SURE:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.WEBTOOLS_MENU_NAVIGATED_TO_MAPSURE
          );
          break;
        case PCM_WEB:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.WEBTOOLS_MENU_NAVIGATED_TO_PC_MILER_WEB
          );
          break;
        case ROUTE_REPORTER:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.WEBTOOLS_MENU_NAVIGATED_TO_ROUTEREPORTER
          );
          break;
        default:
          break;
      }
    } else {
      switch (item.name) {
        case CONTENT_TOOLS:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_UNLICENSED_APP_CONTENT_TOOLS
          );
          break;
        case MAP_EXACT:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_UNLICENSED_APP_MAPEXACT
          );
          break;
        case MAP_SURE:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_UNLICENSED_APP_MAPSURE
          );
          break;
        case PCM_WEB:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_UNLICENSED_APP_PC_MILER_WEB
          );
          break;
        case ROUTE_REPORTER:
          this.brazeEventsService.logBrazeCustomEvents(
            this.brazeEventsService.CLICKED_UNLICENSED_APP_ROUTEREPORTER
          );
          break;
        default:
          break;
      }
    }
  }

  logout() {
    logout();
  }

  handleThemeChanged(theme: Theme) {
    dispatch(setTheme({ theme }));
  }

  ngOnDestroy() {
    resetAppSettingsStore();
    deregisterAppSettingsEffects();

    resetAmsStore();
    deregisterAmsEffects();

    resetSecondaryNavStore();
    deregisterSecondaryNavEffects();

    deregisterAssetsEffects();
    deregisterVehiclesEffects();
    deregisterDriversEffects();

    deregisterContentCardsEffects();
    resetContentCardsStore();

    deregisterVidyardEffects();
    deregisterUserPreferencesEffects();
    this.onboardingTourService.reset();

    this.sm.unsubscribe();
  }
}
